import { ApiClientError } from "@lumberset/api-client";
import { TicketKindBadge, TicketStatusBadge } from "@lumberset/support-ui";
import {
  ago,
  date,
  dateTime,
  money,
  ReadAgain,
  RunStatus,
  reviewStatusLabel,
} from "@lumberset/takeoff-ui";
import { Button, Container, Drawer, EmptyState, SectionHeader, Stat, Table } from "@lumberset/ui";
import {
  ArchiveBox,
  ArrowUpRightOnBox,
  Buildings,
  DocumentText,
  Lifebuoy,
  PauseSolid,
  ScrollText,
  XCircle,
} from "@medusajs/icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, type ErrorComponentProps, Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { auditSentence } from "../../../components/audit";
import { Crumbs } from "../../../components/crumbs";
import { SignInAsPrompt } from "../../../components/sign-in-as";
import {
  useRefreshWorkspace,
  type WorkspaceDetail,
  workspaceActions,
  workspaceQuery,
} from "../../../lib/queries-workspaces";
import { WorkspaceActions } from "./-actions";
import { BillingSection } from "./-billing";
import { EditWorkspace } from "./-edit";
import { MembersSection } from "./-members";
import { bytes, Detail, detailGrid, plural, WorkspaceStatus } from "./-parts";
import { ProjectsSection } from "./-projects";

export const Route = createFileRoute("/_app/yards/$id")({
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData(workspaceQuery(params.id)),
  component: WorkspaceScreen,
  errorComponent: WorkspaceError,
});

/** One workspace, all of it: people, projects, takeoffs, billing, tickets and what Admins did. */
function WorkspaceScreen() {
  const { id } = Route.useParams();
  const { data } = useSuspenseQuery(workspaceQuery(id));
  const { workspace, subscription, state, owner, archive } = data;
  const { user: me } = Route.useRouteContext();
  const [editing, setEditing] = useState(false);
  const [openingAsOwner, setOpeningAsOwner] = useState(false);
  // Why Open as owner cannot be used, or null when it can.
  const ownerRefusal =
    owner === null
      ? null
      : owner.userId === me.id
        ? "You are its Owner."
        : (owner.signInRefusal ?? (state.closed ? "The workspace is closed." : null));

  return (
    <>
      <Crumbs trail={[{ label: "Workspaces", to: "/yards" }, { label: workspace.name }]} />

      {archive?.mergedInto ? (
        <Notice
          icon={<ArchiveBox className="text-ui-fg-muted" />}
          title={
            <>
              This workspace was merged into{" "}
              <Link
                to="/yards/$id"
                params={{ id: archive.mergedInto.id }}
                className="text-ui-fg-interactive hover:underline"
              >
                {archive.mergedInto.name}
              </Link>
            </>
          }
          text={`Its projects, quotes, people and billing history moved there on ${date(archive.archivedAt)}${archive.archivedByName ? `, by ${archive.archivedByName}` : ""}. It stays archived, out of every list, so nothing is lost and nothing shows twice.`}
        />
      ) : archive ? (
        <Notice
          icon={<ArchiveBox className="text-ui-fg-muted" />}
          title="This workspace is archived"
          text={`${archive.archivedByName ?? "An Admin"} archived it on ${date(archive.archivedAt)}${archive.note ? `: ${archive.note}` : ""}. Staff do not see it and it is paused; its members can still sign in and see their projects. Restore it from the Actions menu to bring it back as it was.`}
        />
      ) : state.closed ? (
        <Notice
          icon={<XCircle className="text-ui-fg-muted" />}
          title="This workspace is closed"
          text="Nobody can sign in to it, and its drawings were deleted. Its billing history, projects and tickets are kept."
        />
      ) : subscription?.status === "paused" ? (
        <Notice
          icon={<PauseSolid className="text-ui-tag-orange-icon" />}
          title="This workspace is paused"
          text="Its members can sign in and see their work, but cannot start takeoffs until you reactivate it from the Actions menu."
        />
      ) : null}

      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title={<span className="break-words">{workspace.name}</span>}
          description={
            // The state sits under the name: beside the buttons it would push a phone's row too wide.
            <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
              <WorkspaceStatus
                status={subscription?.status ?? null}
                closed={state.closed}
                archived={state.archived}
                merged={Boolean(archive?.mergedInto)}
              />
              <span>
                {kindText(workspace)}, signed up {date(workspace.createdAt)}.
              </span>
            </span>
          }
          actions={
            <>
              {owner ? (
                <Button
                  size="small"
                  disabled={ownerRefusal !== null}
                  title={ownerRefusal ?? `Signs in as ${owner.name}, its Owner, in the Yard app`}
                  onClick={() => setOpeningAsOwner(true)}
                >
                  <ArrowUpRightOnBox /> Open as owner
                </Button>
              ) : null}
              <WorkspaceActions detail={data} onEdit={() => setEditing(true)} />
            </>
          }
        />
        <Overview detail={data} />
        <Profile detail={data} />
      </Container>

      <Drawer open={editing} onOpenChange={setEditing}>
        <Drawer.Content>
          {editing ? <EditWorkspace detail={data} onClose={() => setEditing(false)} /> : null}
        </Drawer.Content>
      </Drawer>
      <SignInAsPrompt
        person={openingAsOwner && owner ? { userId: owner.userId, name: owner.name } : null}
        surface="app"
        workspaces={[{ id: workspace.id, name: workspace.name }]}
        adminName={me.name}
        onClose={() => setOpeningAsOwner(false)}
      />

      <MembersSection detail={data} />
      <ProjectsSection detail={data} />
      <TakeoffsSection detail={data} />
      <BillingSection detail={data} />
      <TicketsSection detail={data} />
      <ActivitySection detail={data} />
    </>
  );
}

function Notice({ icon, title, text }: { icon: ReactNode; title: ReactNode; text: string }) {
  return (
    <Container className="flex items-start gap-x-3">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="txt-compact-small-plus text-ui-fg-base">{title}</p>
        <p className="txt-small mt-0.5 text-ui-fg-subtle">{text}</p>
      </div>
    </Container>
  );
}

const tradeLabels: Record<string, string> = {
  general_contractor: "general contractor",
  framer: "framer",
  developer: "developer",
  remodeler: "remodeler",
  other: "other trade",
};

function kindText(workspace: WorkspaceDetail["workspace"]) {
  if (workspace.kind !== "builder") return "Lumber yard";
  const trade = workspace.trade ? tradeLabels[workspace.trade] : null;
  return trade ? `Builder (${trade})` : "Builder";
}

/* ---- Overview ------------------------------------------------------------ */

function Overview({ detail }: { detail: WorkspaceDetail }) {
  const { workspace, counts, billing, subscription } = detail;
  const runs = counts.runsByStatus;
  const runHint = [
    runs.released ? `${runs.released} released` : null,
    runs.in_review ? `${runs.in_review} in review` : null,
    runs.queued + runs.running ? `${runs.queued + runs.running} reading` : null,
    runs.failed ? `${runs.failed} failed` : null,
    // Yards and builders both revise drawings, so change orders sit with the takeoffs.
    counts.changeOrders ? plural(counts.changeOrders, "change order") : null,
  ]
    .filter(Boolean)
    .join(", ");
  const builder = workspace.kind === "builder";
  // Borders come from the 1px gaps; ten tiles fill two rows of five or five rows of two.
  const tile = "min-w-0 bg-ui-bg-base px-4 sm:px-6";
  // A large amount is cut short with an ellipsis rather than spill into the next tile on a phone.
  const amount = (cents: number) => (
    <span className="block truncate" title={money(cents)}>
      {money(cents)}
    </span>
  );

  return (
    <div className="grid grid-cols-2 gap-px bg-ui-border-base sm:grid-cols-5">
      <Stat
        className={tile}
        label="Members"
        value={counts.members}
        hint={counts.invitations ? `${counts.invitations} invited` : "People who sign in"}
      />
      <Stat
        className={tile}
        label="Projects"
        value={counts.projects}
        hint={plural(counts.planSets, "plan set")}
      />
      <Stat className={tile} label="Takeoffs" value={counts.runs} hint={runHint || "None yet"} />
      {builder ? (
        <Stat className={tile} label="RFQs" value={counts.rfqs} hint="Sent to yards for pricing" />
      ) : (
        <Stat
          className={tile}
          label="Quotes"
          value={counts.quotes}
          hint={`${counts.acceptedQuotes} accepted`}
        />
      )}
      <Stat
        className={tile}
        label="Drawings stored"
        value={bytes(counts.storageBytes)}
        hint={plural(counts.files, "file")}
      />
      <Stat
        className={tile}
        label="Billed"
        value={amount(billing.billedCents)}
        hint={plural(billing.billableQuotes, "Billable Quote")}
      />
      <Stat
        className={tile}
        label="Trial takeoffs left"
        value={subscription?.trialQuotesRemaining ?? 0}
        hint="Platform credit, used before anything else"
      />
      <Stat
        className={tile}
        label="Takeoff credits"
        value={subscription?.creditQuotes ?? 0}
        hint={
          subscription && subscription.accountCreditCents > 0
            ? `Platform credit, plus ${money(subscription.accountCreditCents)} account credit`
            : "Platform credit, prepaid or granted, used before fees"
        }
      />
      <Stat
        className={tile}
        label="Reading cost"
        value={amount(billing.readingCostCents)}
        hint="What its takeoffs cost us"
      />
      <Stat
        className={tile}
        label="Open tickets"
        value={counts.openTickets}
        hint={plural(detail.tickets.length, "recent ticket")}
      />
    </div>
  );
}

function addressText(address: WorkspaceDetail["workspace"]["address"]) {
  if (!address) return null;
  const parts = [
    address.line1,
    address.line2,
    address.city,
    [address.region, address.postalCode].filter(Boolean).join(" "),
    address.country,
  ].filter((part) => part?.trim());
  return parts.length > 0 ? parts.join(", ") : null;
}

function Profile({ detail }: { detail: WorkspaceDetail }) {
  const { workspace, mergedFrom } = detail;
  const address = addressText(workspace.address);

  return (
    <dl className={detailGrid}>
      {workspace.legalName ? <Detail label="Legal name">{workspace.legalName}</Detail> : null}
      {workspace.email ? (
        <Detail label="Email">
          <a className="text-ui-fg-interactive hover:underline" href={`mailto:${workspace.email}`}>
            {workspace.email}
          </a>
        </Detail>
      ) : null}
      {workspace.phone ? <Detail label="Phone">{workspace.phone}</Detail> : null}
      {workspace.website ? <Detail label="Website">{workspace.website}</Detail> : null}
      {address ? <Detail label="Address">{address}</Detail> : null}
      <Detail label="Review">
        {workspace.reviewMode === "audit"
          ? "Takeoffs are released at once and reviewed after"
          : "Takeoffs wait for a Reviewer before release"}
      </Detail>
      {mergedFrom.length > 0 ? (
        <Detail label="Merged into this one">
          {mergedFrom.map((row, index) => (
            <span key={row.id}>
              {index > 0 ? ", " : null}
              <Link
                to="/yards/$id"
                params={{ id: row.id }}
                className="text-ui-fg-interactive hover:underline"
              >
                {row.name}
              </Link>
              {row.mergedAt ? ` on ${date(row.mergedAt)}` : null}
            </span>
          ))}
        </Detail>
      ) : null}
      <Detail label="Workspace id">
        <code className="txt-compact-xsmall break-all font-mono">{workspace.id}</code>
      </Detail>
    </dl>
  );
}

/* ---- Takeoffs ------------------------------------------------------------ */

function TakeoffsSection({ detail }: { detail: WorkspaceDetail }) {
  const { runs, counts, workspace } = detail;
  const refresh = useRefreshWorkspace(workspace.id);

  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="Takeoffs"
        description={
          counts.archivedProjects > 0
            ? "Newest first, leaving out the archived projects' takeoffs. Cost is what reading the drawings cost us, failed and stopped readings included, and what its AI reading allowance covered of it."
            : counts.runs > runs.length
              ? `The newest ${runs.length} of ${counts.runs}. Cost is what reading the drawings cost us, failed and stopped readings included, and what its AI reading allowance covered of it.`
              : "Newest first. Cost is what reading the drawings cost us, failed and stopped readings included, and what its AI reading allowance covered of it."
        }
      />
      {runs.length === 0 ? (
        <EmptyState
          className="py-10"
          icon={<DocumentText />}
          title="No takeoffs yet"
          description="Takeoffs appear here once the workspace uploads drawings and starts one."
        />
      ) : (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Project</Table.HeaderCell>
              <Table.HeaderCell>Started</Table.HeaderCell>
              <Table.HeaderCell className="text-right">Cost</Table.HeaderCell>
              <Table.HeaderCell>Review</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {runs.map((run) => (
              <Table.Row key={run.id}>
                <Table.Cell className="whitespace-nowrap">
                  <RunStatus status={run.status} />
                </Table.Cell>
                <Table.Cell className="py-2.5">
                  <div className="txt-compact-small-plus max-w-[260px] truncate text-ui-fg-base">
                    {run.projectName}
                  </div>
                  <div className="txt-compact-xsmall max-w-[260px] truncate text-ui-fg-muted">
                    {run.planSetLabel}
                  </div>
                  {run.error ? (
                    <div
                      className="txt-compact-xsmall max-w-[260px] truncate text-ui-fg-error"
                      title={run.error}
                    >
                      {run.error}
                    </div>
                  ) : run.heldFor === "reading_stopped" && run.status === "in_review" ? (
                    <div className="txt-compact-xsmall max-w-[260px] text-ui-tag-orange-text">
                      Reading stopped at the limit: a Reviewer completes it
                    </div>
                  ) : null}
                  {run.readAgain ? (
                    <ReadAgain
                      className="mt-1.5 max-w-[260px]"
                      offer={run.readAgain}
                      projectName={run.projectName}
                      send={() => workspaceActions.readAgain(workspace.id, run.id)}
                      onSent={refresh}
                    />
                  ) : null}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">{dateTime(run.createdAt)}</Table.Cell>
                <Table.Cell className="tabular whitespace-nowrap text-right">
                  {run.costCents === null ? "-" : money(run.costCents)}
                  {run.readingBasis ? (
                    <div className="txt-compact-xsmall text-ui-fg-muted">
                      {run.readingBasis === "trial" ? "Trial takeoff" : "Paid takeoff"}
                    </div>
                  ) : null}
                  {run.allowanceCents > 0 ? (
                    <div className="txt-compact-xsmall text-ui-fg-muted">
                      {money(run.allowanceCents)} from allowance
                    </div>
                  ) : null}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  {run.reviewUrl ? (
                    <a
                      href={run.reviewUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="txt-compact-small-plus inline-flex items-center gap-x-1 text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
                    >
                      Open review <ArrowUpRightOnBox />
                    </a>
                  ) : (
                    <span className="text-ui-fg-muted">-</span>
                  )}
                  {run.reviewStatus ? (
                    <div className="txt-compact-xsmall text-ui-fg-muted">
                      {reviewStatusLabel(run.reviewStatus)}
                    </div>
                  ) : null}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Container>
  );
}

/* ---- Tickets ------------------------------------------------------------- */

function TicketsSection({ detail }: { detail: WorkspaceDetail }) {
  const { tickets } = detail;

  return (
    <Container className="divide-y p-0">
      <SectionHeader title="Tickets" description="The latest 20 from or to this workspace." />
      {tickets.length === 0 ? (
        <EmptyState
          className="py-10"
          icon={<Lifebuoy />}
          title="No tickets"
          description="Questions and problems this workspace reports appear here."
        />
      ) : (
        <ul className="divide-y divide-ui-border-base">
          {tickets.map((row) => (
            <li key={row.id}>
              <Link
                to="/support/tickets/$id"
                params={{ id: row.id }}
                className="transition-fg flex flex-wrap items-center gap-x-3 gap-y-1.5 px-6 py-3 outline-none hover:bg-ui-bg-base-hover focus-visible:bg-ui-bg-base-hover"
              >
                <span className="min-w-0 flex-1 basis-[200px]">
                  <span className="txt-compact-small-plus block truncate text-ui-fg-base">
                    {row.title}
                  </span>
                  <span className="txt-compact-xsmall block text-ui-fg-muted">
                    Opened {date(row.createdAt)}, last activity {ago(row.lastActivityAt)}
                  </span>
                </span>
                <span className="flex items-center gap-x-2">
                  <TicketKindBadge kind={row.kind} />
                  <TicketStatusBadge status={row.status} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Container>
  );
}

/* ---- Activity ------------------------------------------------------------ */

function ActivitySection({ detail }: { detail: WorkspaceDetail }) {
  const { activity } = detail;

  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="Activity"
        description="What Admins, Staff and the workspace's own people changed here, newest first. The latest 100 entries."
      />
      {activity.length === 0 ? (
        <EmptyState
          className="py-10"
          icon={<ScrollText />}
          title="Nothing recorded yet"
          description="Changes to this workspace's people, projects and billing land here."
        />
      ) : (
        <ul className="divide-y divide-ui-border-base">
          {activity.map((entry) => {
            const hasDetails = entry.data !== null && Object.keys(entry.data).length > 0;
            return (
              <li key={entry.id} className="px-6 py-3">
                <p className="txt-compact-small break-words text-ui-fg-base" title={entry.action}>
                  {auditSentence(entry.action, entry.data as Record<string, unknown> | null)}
                </p>
                <p className="txt-compact-xsmall text-ui-fg-muted">
                  {entry.actorName ?? "System"}, {dateTime(entry.createdAt)}
                </p>
                {/* The exact record stays one click away, folded, for whoever needs it. */}
                {hasDetails ? (
                  <details className="group mt-1">
                    <summary className="txt-compact-xsmall w-fit cursor-pointer select-none rounded text-ui-fg-muted outline-none hover:text-ui-fg-subtle focus-visible:shadow-borders-focus">
                      Details
                    </summary>
                    <pre className="txt-compact-xsmall mt-1.5 max-h-60 overflow-auto whitespace-pre-wrap break-all rounded-md bg-ui-bg-subtle px-3 py-2 font-mono text-ui-fg-subtle">
                      {JSON.stringify(entry.data, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Container>
  );
}

/* ---- Errors -------------------------------------------------------------- */

function WorkspaceError({ error, reset }: ErrorComponentProps) {
  const missing = error instanceof ApiClientError && error.status === 404;
  return (
    <Container>
      <EmptyState
        icon={<Buildings />}
        title={missing ? "No such workspace" : "This workspace could not be loaded"}
        description={
          missing
            ? "It may have been deleted. Its entries stay in the audit log."
            : error instanceof Error
              ? error.message
              : "Please try again."
        }
        action={
          missing ? (
            <Button variant="secondary" asChild>
              <Link to="/yards">Back to Workspaces</Link>
            </Button>
          ) : (
            <Button variant="secondary" onClick={reset}>
              Try again
            </Button>
          )
        }
      />
    </Container>
  );
}
